/**
 * The entry card: a ledger line, opened without leaving the page you were reading.
 *
 * Clicking a statement line used to navigate to the invoice, which answers "what did we sell" and
 * not "why does this customer still owe 240 of it". The drawer answers the second question — what
 * the entry was worth, what has been applied to it and by which payment, which accounts it moved —
 * and only then offers to open the document.
 *
 * "Find entries" is Business Central's Navigate: every record in the system carrying this document
 * number, whichever module holds it, asked for in one request.
 */
import { date, label as fmt, type JournalEntry, money, useApi, useAuth } from "@keel/core";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Pill, Skeleton } from "./ui";

export interface LedgerEntry {
  kind: string;
  id: string;
  number: string;
  date: string;
  dueDate: string | null;
  partyId: string | null;
  partyName: string | null;
  partyKind: string | null;
  description: string | null;
  status: string;
  original: number;
  applied: number;
  remaining: number;
  controlAccount: string | null;
  applications: {
    kind: string;
    id: string;
    number: string;
    date: string;
    amount: number;
    note: string | null;
  }[];
  postings: JournalEntry[];
}

export interface TrailHit {
  kind: string;
  id: string;
  number: string;
  date: string | null;
  title: string;
  subtitle: string | null;
  amount: number | null;
  status: string | null;
  isOrigin?: boolean;
}
export interface Navigation {
  number: string | null;
  total: number;
  groups: { kind: string; label: string; count: number; hits: TrailHit[] }[];
}

/** What the drawer was opened on. `kind` is a ledger entry kind or `journal_entry`. */
export interface EntryRef {
  kind: string;
  id: string;
  /** Shown in the header before the entry loads, and used for "find entries". */
  number?: string;
}

const Ctx = createContext<(e: EntryRef | null) => void>(() => {});

/**
 * Put this once near the top of the app; any page below can then call `useEntry()` and hand it a
 * record, instead of every table growing its own drawer.
 */
export function EntryDrawerHost({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<EntryRef | null>(null);
  return (
    <Ctx.Provider value={setEntry}>
      {children}
      {entry && (
        <EntryDrawer key={`${entry.kind}:${entry.id}`} entry={entry} onClose={() => setEntry(null)} />
      )}
    </Ctx.Provider>
  );
}

/** `const open = useEntry(); open({ kind: "invoice", id, number })`. */
export const useEntry = () => useContext(Ctx);

/**
 * Where a kind's own page lives, for the "Open" button and the trail links. A value ending in `/`
 * takes the record's id; the rest are list pages, because not every kind has a page of its own and
 * a link that lands on the right list beats a link that 404s.
 */
const ROUTE: Record<string, string> = {
  invoice: "/invoices/",
  bill: "/bills/",
  credit_note: "/invoices/",
  payment: "/payments",
  order: "/orders/",
  purchase_order: "/purchase-orders/",
  delivery: "/deliveries/",
  journal_entry: "/journal",
  stock_movement: "/stock-movements",
  stock_count: "/stock-counts/",
  putaway: "/putaway",
  statement_line: "/bank-statements",
  loan: "/loans/",
  asset: "/assets/",
};
const ICON: Record<string, string> = {
  invoice: "invoice",
  bill: "receipt",
  credit_note: "receipt",
  payment: "wallet",
  order: "cart",
  purchase_order: "factory",
  delivery: "truck",
  journal_entry: "ledger",
  stock_movement: "moves",
  stock_count: "check",
  putaway: "boxes",
  statement_line: "cash",
  loan: "cash",
  asset: "building",
};
const hrefOf = (kind: string, id: string) => {
  const r = ROUTE[kind];
  return r ? (r.endsWith("/") ? r + id : r) : null;
};

function EntryDrawer({ entry, onClose }: { entry: EntryRef; onClose: () => void }) {
  const [tab, setTab] = useState<"entry" | "trail">("entry");
  // Escape closes it wherever focus is; the drawer is not a dialog, so nothing does that for us.
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  return (
    <>
      {/* A button, not a div: clicking away is a real action and wants a real control. */}
      <button type="button" className="drawer-scrim" aria-label="Close entry" onClick={onClose} />
      <aside className="drawer" aria-label="Entry">
        <header className="drawer-head">
          <div className="row" style={{ gap: 8 }}>
            <Icon name={ICON[entry.kind] ?? "file"} size={16} />
            <div>
              <h2>{entry.number ?? fmt(entry.kind)}</h2>
              <p className="sub">{fmt(entry.kind)}</p>
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </header>
        <nav className="drawer-tabs">
          <button type="button" className={tab === "entry" ? "on" : ""} onClick={() => setTab("entry")}>
            The entry
          </button>
          <button type="button" className={tab === "trail" ? "on" : ""} onClick={() => setTab("trail")}>
            Find entries
          </button>
        </nav>
        <div className="drawer-body">
          {tab === "entry" ? <Entry entry={entry} /> : <Trail entry={entry} />}
        </div>
      </aside>
    </>
  );
}

function Fig({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="drawer-fig">
      <span>{label}</span>
      <strong className={tone ? `tone-${tone}` : ""}>{value}</strong>
    </div>
  );
}

function Entry({ entry }: { entry: EntryRef }) {
  if (entry.kind === "journal_entry") return <JournalCard id={entry.id} />;
  return <SubledgerCard entry={entry} />;
}

function SubledgerCard({ entry }: { entry: EntryRef }) {
  const { data } = useApi<LedgerEntry>(`/ledger-entries/${entry.kind}/${entry.id}`);
  if (!data) return <Skeleton rows={4} />;
  const open = data.remaining;
  const late = data.dueDate && open > 0.005 && new Date(data.dueDate) < new Date();
  const href = hrefOf(data.kind, data.id);
  return (
    <>
      <section className="drawer-figs">
        <Fig label="Original" value={money(data.original)} />
        <Fig label={data.kind === "payment" ? "Allocated" : "Paid"} value={money(data.applied)} />
        <Fig
          label={data.kind === "payment" ? "On account" : "Still open"}
          value={money(open)}
          tone={open > 0.005 ? (late ? "danger" : "warn") : undefined}
        />
      </section>

      <dl className="drawer-pairs">
        <dt>Status</dt>
        <dd>
          <Pill status={data.status} />
        </dd>
        <dt>Posted</dt>
        <dd>{date(data.date)}</dd>
        {data.dueDate && (
          <>
            <dt>Due</dt>
            <dd className={late ? "tone-danger strong" : ""}>{date(data.dueDate)}</dd>
          </>
        )}
        {data.partyName && (
          <>
            <dt>{data.partyKind === "supplier" ? "Supplier" : "Customer"}</dt>
            <dd>
              {data.partyId ? (
                <Link to={`/${data.partyKind === "supplier" ? "suppliers" : "customers"}/${data.partyId}`}>
                  {data.partyName}
                </Link>
              ) : (
                data.partyName
              )}
            </dd>
          </>
        )}
        {data.controlAccount && (
          <>
            <dt>Control account</dt>
            <dd className="mono">{data.controlAccount}</dd>
          </>
        )}
        {data.description && (
          <>
            <dt>Reference</dt>
            <dd>{data.description}</dd>
          </>
        )}
      </dl>

      <Block
        title={data.kind === "payment" ? "Settles" : "Applied by"}
        empty={
          data.kind === "payment"
            ? "Nothing allocated — the whole amount is on account."
            : "Nothing has been applied to this yet."
        }
        rows={data.applications.map((a) => ({
          key: a.id,
          href: hrefOf(a.kind, a.id),
          left: <span className="mono">{a.number}</span>,
          mid: (
            <>
              {date(a.date)}
              {a.note && (
                <span className="pill pill-void" style={{ marginLeft: 6 }}>
                  {a.note}
                </span>
              )}
            </>
          ),
          right: money(a.amount),
        }))}
      />

      <Postings postings={data.postings} />

      {href && (
        <div className="drawer-foot">
          <Link className="btn btn-primary" to={href}>
            Open {fmt(data.kind).toLowerCase()}
          </Link>
        </div>
      )}
    </>
  );
}

function JournalCard({ id }: { id: string }) {
  const { data } = useApi<JournalEntry>(`/accounting/journal/${id}`);
  if (!data) return <Skeleton rows={4} />;
  const total = data.lines.reduce((s, l) => s + l.debit, 0);
  return (
    <>
      <section className="drawer-figs">
        <Fig label="Debits" value={money(total)} />
        <Fig label="Credits" value={money(data.lines.reduce((s, l) => s + l.credit, 0))} />
        <Fig label="Lines" value={data.lines.length} />
      </section>
      <dl className="drawer-pairs">
        <dt>Posted</dt>
        <dd>{date(data.date)}</dd>
        <dt>Memo</dt>
        <dd>{data.memo}</dd>
        {data.createdBy && (
          <>
            <dt>By</dt>
            <dd>{data.createdBy}</dd>
          </>
        )}
        {data.sourceNumber && (
          <>
            <dt>Source</dt>
            <dd>
              <span className="mono">{data.sourceNumber}</span>{" "}
              <span className="muted sm">{fmt(data.sourceType ?? "")}</span>
            </dd>
          </>
        )}
        {(data.reversesId || data.reversedById) && (
          <>
            <dt>Reversal</dt>
            <dd>{data.reversesId ? "Reverses an earlier entry" : "Reversed by a later entry"}</dd>
          </>
        )}
      </dl>
      <Postings postings={[data]} />
    </>
  );
}

function Postings({ postings }: { postings: JournalEntry[] }) {
  const { can } = useAuth();
  if (!can("accounting.journal.read")) return null;
  if (postings.length === 0) {
    return (
      <Block title="General ledger" empty="Nothing posted — this entry is not in the books." rows={[]} />
    );
  }
  return (
    <section className="drawer-block">
      <h3>General ledger</h3>
      {postings.map((p) => (
        <div key={p.id} className="drawer-posting">
          <div className="drawer-posting-head">
            <span className="mono strong">{p.number}</span>
            <span className="muted sm">{date(p.date)}</span>
          </div>
          <table className="table table-tight">
            <tbody>
              {p.lines.map((l, i) => (
                <tr key={`${p.id}-${i}`}>
                  <td className="mono">{l.accountCode}</td>
                  <td className="muted">{l.accountName}</td>
                  <td className="r num">{l.debit ? money(l.debit) : ""}</td>
                  <td className="r num">{l.credit ? money(l.credit) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

function Block({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: { key: string; href: string | null; left: ReactNode; mid: ReactNode; right: ReactNode }[];
}) {
  return (
    <section className="drawer-block">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="sub">{empty}</p>
      ) : (
        <ul className="drawer-list">
          {rows.map((r) => (
            <li key={r.key}>
              {r.href ? (
                <Link to={r.href}>
                  <span>{r.left}</span>
                  <span className="muted sm">{r.mid}</span>
                  <span className="num">{r.right}</span>
                </Link>
              ) : (
                <span className="drawer-row">
                  <span>{r.left}</span>
                  <span className="muted sm">{r.mid}</span>
                  <span className="num">{r.right}</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Trail({ entry }: { entry: EntryRef }) {
  const open = useEntry();
  const q = new URLSearchParams();
  if (entry.number) q.set("number", entry.number);
  q.set("sourceType", entry.kind);
  q.set("sourceId", entry.id);
  const { data } = useApi<Navigation>(`/navigate?${q}`);
  if (!data) return <Skeleton rows={4} />;
  if (data.total === 0) {
    return <p className="sub">Nothing else in the system carries this document.</p>;
  }
  return (
    <>
      <p className="sub">
        {data.total === 1 ? "One record carries" : `${data.total} records carry`} this document.
      </p>
      {data.groups.map((g) => (
        <section className="drawer-block" key={g.kind}>
          <h3>
            {g.label} <span className="muted">{g.count}</span>
          </h3>
          <ul className="drawer-list">
            {g.hits.map((h) => {
              const body = (
                <>
                  <span>
                    <span className="mono">{h.number}</span>
                    {h.isOrigin && (
                      <span className="perm-tag" style={{ marginLeft: 6 }}>
                        this
                      </span>
                    )}
                    <div className="sm muted">{h.title}</div>
                  </span>
                  <span className="muted sm">
                    {h.date ? date(h.date) : ""}
                    {h.subtitle ? ` · ${h.subtitle}` : ""}
                  </span>
                  <span className="num">
                    {h.amount == null ? "" : money(h.amount)}
                    {h.status && <div className="sm muted">{fmt(h.status)}</div>}
                  </span>
                </>
              );
              // A ledger entry or a journal entry opens in this drawer; anything else is a page.
              const inDrawer = ["invoice", "bill", "payment", "journal_entry"].includes(h.kind);
              const href = hrefOf(h.kind, h.id);
              return (
                <li key={`${h.kind}-${h.id}`}>
                  {inDrawer ? (
                    <button
                      type="button"
                      className="drawer-row as-link"
                      onClick={() => open({ kind: h.kind, id: h.id, number: h.number })}
                    >
                      {body}
                    </button>
                  ) : href ? (
                    <Link to={href}>{body}</Link>
                  ) : (
                    <span className="drawer-row">{body}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}
